import React, { useRef, useCallback } from 'react';
import { View, Pressable, Platform } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { selectUser } from '@reduxLocal/selectors/userSelectors';
import apis from '@api/api';
import QuickAddDataModal, { QuickAddHandle } from '@components/modals/QuickAddDataModal/QuickAddDataModal';
import DataChart from '@components/dataTables/DataChart';
import { transformStatsForChart, hasChartData } from '@utils/chartDataTransformer';
import { MaterialIcons } from '@expo/vector-icons';
import { FontAwesome } from '@expo/vector-icons';
import { Text, SizableText, YStack, XStack } from 'tamagui';
import toTitleCase from '@hooks/toTitleCase';
import parseByDelimiter from '@utils/parseByDelimiter';

interface DataCollectionRequestProps {
  dataLink: {
    _id: string;
    dataTitle?: string;
  };
  theme: boolean;
  currentTheme: any;
  responsive: {
    spacing: {
      xs: number;
      sm: number;
      md: number;
      lg: number;
      xl?: number;
    };
    typography: {
      xs: number;
      sm: number;
      base: number;
      lg?: number;
    };
    components: {
      iconSize: number;
    };
  };
  TYPOGRAPHY: any;
  SHADOWS: any;
}

const DataCollectionRequest: React.FC<DataCollectionRequestProps> = ({
  dataLink,
  theme,
  currentTheme,
  responsive,
  TYPOGRAPHY,
  SHADOWS,
}) => {
  const quickAddRef = useRef<QuickAddHandle>(null);
  const currentUser = selectUser();

  // Fetch folder stats for chart
  const { data: folderStats, isLoading: statsLoading } = useQuery({
    queryKey: ['folderStats', dataLink._id],
    queryFn: () => apis.getFolderStats(dataLink._id),
    enabled: !!dataLink._id,
    staleTime: 60000, // 1 minute
  });

  // Fetch full dataset object for QuickAddDataModal
  const { data: datasetItem } = useQuery({
    queryKey: ['dataFolder', dataLink._id],
    queryFn: async () => {
      if (!dataLink._id || !currentUser?._id) return null;
      const folders = await apis.getDataFolders({ userId: currentUser._id, dataFilter: 'all' });
      return folders?.find((folder: any) => folder._id === dataLink._id) || null;
    },
    enabled: !!dataLink._id && !!currentUser?._id,
    staleTime: 300000, // 5 minutes
  });

  // Handler to open contribute modal
  const handleContributeData = useCallback(() => {
    if (datasetItem) {
      quickAddRef.current?.openModal(datasetItem);
    }
  }, [datasetItem]);

  if (!dataLink?._id) return null;

  // Transform chart data for native
  const chartData = Platform.OS === 'web'
    ? folderStats?.statsStructure
    : transformStatsForChart(folderStats?.statsStructure);

  const hasData = hasChartData(chartData);

  return (
    <>
      <View
        style={{
          marginHorizontal: responsive.spacing.sm,
          marginTop: responsive.spacing.md,
          marginBottom: responsive.spacing.xs,
          padding: responsive.spacing.md,
          backgroundColor: theme ? 'rgba(90,192,234,0.08)' : 'rgba(90,192,234,0.15)',
          borderRadius: responsive.spacing.md,
          borderWidth: 2,
          borderColor: currentTheme.primary,
          ...SHADOWS.light,
        }}
      >
        {/* Dataset Header */}
        <XStack
          alignItems="center"
          justifyContent="space-between"
          marginBottom={responsive.spacing.sm}
        >
          <View style={{ flex: 1 }}>
            <Text
              style={{
                ...TYPOGRAPHY.subheading,
                fontSize: responsive.typography.base,
                fontWeight: '700',
                color: currentTheme.primary,
                marginBottom: 4,
              }}
            >
              📊 {toTitleCase(parseByDelimiter(dataLink.dataTitle || 'Dataset'))}
            </Text>
            <XStack alignItems="center" gap={responsive.spacing.xs}>
              <SizableText
                fontSize={responsive.typography.xs}
                color={currentTheme.onSurfaceVariant}
              >
                {folderStats?.count || 0} items
              </SizableText>
              {datasetItem?.owner && (
                <>
                  <View
                    style={{
                      width: 4,
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: currentTheme.onSurfaceVariant,
                    }}
                  />
                  <SizableText
                    fontSize={responsive.typography.xs}
                    color={currentTheme.onSurfaceVariant}
                  >
                    by {toTitleCase(datasetItem.owner.profile_information?.name || '')} {toTitleCase(datasetItem.owner.profile_information?.surname || '')}
                  </SizableText>
                </>
              )}
            </XStack>
          </View>
        </XStack>

        {/* Chart Section */}
        <View
          style={{
            marginVertical: responsive.spacing.sm,
            borderRadius: responsive.spacing.sm,
            overflow: 'hidden',
            minHeight: 200,
            backgroundColor: theme ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.2)',
          }}
        >
          {statsLoading ? (
            <View
              style={{
                height: 200,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SizableText
                fontSize={responsive.typography.sm || 14}
                color={currentTheme.onSurfaceVariant}
              >
                Loading chart...
              </SizableText>
            </View>
          ) : hasData ? (
            <DataChart
              chartOnly
              hideLabels
              reportData={chartData || {}}
              state="month"
            />
          ) : (
            <View
              style={{
                height: 200,
                alignItems: 'center',
                justifyContent: 'center',
                padding: responsive.spacing.md,
              }}
            >
              <MaterialIcons
                name="insert-chart"
                size={32}
                color={currentTheme.onSurfaceVariant}
              />
              <SizableText
                fontSize={responsive.typography.sm || 14}
                color={currentTheme.onSurfaceVariant}
                marginTop={responsive.spacing.sm}
              >
                No data yet
              </SizableText>
            </View>
          )}
        </View>

        {/* Contribute Button */}
        <Pressable
          onPress={handleContributeData}
          disabled={!datasetItem}
          style={{
            marginTop: responsive.spacing.sm,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            paddingVertical: responsive.spacing.md,
            paddingHorizontal: responsive.spacing.lg,
            borderRadius: responsive.spacing.md,
            backgroundColor: datasetItem
              ? 'rgb(90,192,234)'
              : 'rgb(90,192,234)',
            gap: responsive.spacing.sm,
            opacity: datasetItem ? 1 : 0.5,
            ...SHADOWS.light,
          }}
        >
          <FontAwesome
            name="plus-circle"
            size={responsive.components.iconSize || 20}
            color="white"
          />
          <Text
            style={{
              ...TYPOGRAPHY.body,
              fontSize: responsive.typography.base || 16,
              fontWeight: '600',
              color: 'white',
            }}
          >
            Contribute to Dataset
          </Text>
        </Pressable>
      </View>

      {/* QuickAddDataModal */}
      <QuickAddDataModal ref={quickAddRef} />
    </>
  );
};

export default DataCollectionRequest;

